/**
 * RelatedSpheresEditorMolecule.tsx
 *
 * Editor for Related Spheres factor configuration.
 * Card order: the Rollup Set Card (the single authoring surface for a factor's population —
 * scope, direction, narrowing tree, pins, reads-as; EAINT-877 D59), the upward card ("Where this
 * rolls up", only on a sphere that hosts a measure; EAINT-940), the roster, then
 * 1. How members combine (which result wins, how much each member counts)
 * 2. Trust guardrails
 * 3. Edge cases (empty population, read-through)
 */

import React, { useCallback, useState } from 'react';
import type { ReadsAsExclusions } from '../RollupSetCard/readsAsSentence';
import RollupSetCardMolecule from '../RollupSetCard/RollupSetCardMolecule';
import type { RollupSetModel } from '../RollupSetCard/rollupSetModel';
import RollupCombineSection from './RollupCombineSection';
import RollupUpwardCard from './RollupUpwardCard';
import RollupRosterMolecule from '../RollupRoster/RollupRosterMolecule';
import type { RosterSummary } from '../RollupRoster/RollupRosterMolecule';
import RollupGuardrailsSection from './RollupGuardrailsSection';
import RollupEdgeCasesSection from './RollupEdgeCasesSection';
import type { FactorFormState, RosterCombineOverrides } from '../../../types/statusFactors';
import type { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { PerspectiveOption as PerspectiveFilterOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import type { FilterGroupLeaf } from 'types/filters';
import type { RosterCoverage } from 'types/spherePoints';
import './RelatedSpheresEditorMolecule.scss';

/**
 * Arc/relationship have no persisted lane on this factor (`FactorFormState` carries no
 * `arcFilters`/`relationshipFilters`, and this editor receives no `arcTemplates`/
 * `availableRelationships` catalog to author them against) — never a data-shape the
 * NARROWED TO tree could round-trip. Perspective is authored inside the set card's own
 * bands, not through this property list.
 */
const EXCLUDED_RELATED_SPHERES_PROPERTY_TYPES: PropertyType[] = ['arc', 'relationship', 'perspective'];

export interface RelatedSpheresEditorMoleculeProps {
  /** Current form state */
  formState: FactorFormState;
  /** Callback when form state changes */
  onChange: (updates: Partial<FactorFormState>) => void;
  /** Available properties for filters */
  availableProperties: PropertyOption[];
  /** Available perspectives for filters */
  availablePerspectives: PerspectiveFilterOption[];
  /** Props for perspective search (async loading) */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
  };
  /** Whether the editor is disabled */
  disabled?: boolean;
  /**
   * The tree's "Add condition" catalog — the host supplies the same six-lane
   * `AspectCatalogMolecule` every other surface mounts, narrowed to the lanes
   * `allowedLanesForSurface('factor-related-spheres')` names. Without it the
   * tree's add affordance opens nothing.
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  /** In-scope sphere type labels — forwarded to the tree's embedded catalog (see `UnifiedFilterTreeMolecule`). */
  scopeTypeLabels?: string[];
  /** READS AS's server-computed roster coverage — forwarded to the Rollup Set Card verbatim. */
  coverage?: RosterCoverage;
  /** True while local edits haven't been applied/saved yet — forwarded to the Rollup Set Card verbatim. */
  isDirty?: boolean;
  /** The sphere the factor belongs to; the roster mounts only once this and a saved `formState.factorId` exist. */
  sphereId?: string;
  /** Unsaved combine settings the roster rows follow; undefined unless the caller may edit (see `buildRosterOverrides`). */
  rosterOverrides?: RosterCombineOverrides;
  /** True when this sphere hosts a measure, so it can itself be a member of one — mounts "Where this rolls up". */
  hostsMeasure?: boolean;
  /** Additional CSS class names */
  className?: string;
}

const RelatedSpheresEditorMolecule: React.FC<RelatedSpheresEditorMoleculeProps> = ({
  formState,
  onChange,
  availableProperties,
  availablePerspectives,
  perspectiveSearchProps,
  disabled = false,
  className,
  renderCatalog,
  scopeTypeLabels,
  coverage,
  isDirty,
  sphereId,
  rosterOverrides,
  hostsMeasure = false,
}) => {
  const [excluded, setExcluded] = useState<ReadsAsExclusions>();
  const [rosterSummary, setRosterSummary] = useState<RosterSummary>();

  // The set card owns the factor's whole population — scope, direction, narrowing tree and
  // pins — as one model; every edit replaces `rollupSet` wholesale (EAINT-877 D59).
  const handleRollupSetChange = useCallback(
    (next: RollupSetModel) => onChange({ rollupSet: next }),
    [onChange]
  );

  const classes = [
    'related-spheres-editor',
    disabled && 'related-spheres-editor--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {/* Section 1: the Rollup Set Card — scope, direction, narrowing tree, pins, reads-as */}
      <RollupSetCardMolecule
        value={formState.rollupSet}
        onChange={handleRollupSetChange}
        availableProperties={availableProperties}
        excludePropertyTypes={EXCLUDED_RELATED_SPHERES_PROPERTY_TYPES}
        availablePerspectives={availablePerspectives}
        perspectiveSearchProps={perspectiveSearchProps}
        renderCatalog={renderCatalog}
        scopeTypeLabels={scopeTypeLabels}
        disabled={disabled}
        coverage={coverage}
        isDirty={isDirty}
        excluded={excluded}
      />
      {hostsMeasure && sphereId && <RollupUpwardCard sphereId={sphereId} />}
      {sphereId && formState.factorId && (
        <RollupRosterMolecule
          sphereId={sphereId}
          factorId={formState.factorId}
          setModel={formState.rollupSet}
          relationLabel={
            availablePerspectives.find((p) => p.systemName === formState.rollupSet.perspectiveSystemName)?.label
          }
          aggregationMethod={formState.aggregationMethod}
          weighting={formState.weighting}
          factorWeight={formState.weight}
          onExclusionsChange={setExcluded}
          onRosterSummaryChange={setRosterSummary}
          disabled={disabled}
          combineOverrides={rosterOverrides}
        />
      )}

      {/* Section 2: How members combine */}
      <RollupCombineSection
        aggregationMethod={formState.aggregationMethod}
        weighting={formState.weighting}
        onChange={onChange}
        disabled={disabled}
      >
      <RollupGuardrailsSection
        aggregationMethod={formState.aggregationMethod}
        weighting={formState.weighting}
        influenceFloor={formState.influenceFloor}
        coverageFloor={formState.coverageFloor}
        coverage={rosterSummary?.coverage}
        edgeBackedCount={rosterSummary?.edgeBackedCount}
        diagnostics={isDirty ? undefined : rosterSummary?.diagnostics}
        contributionUnavailableCount={rosterSummary?.contributionUnavailableCount}
        onChange={onChange}
        disabled={disabled}
      />
      </RollupCombineSection>

      {/* Section 4: Edge cases */}
      <RollupEdgeCasesSection
        emptyBehavior={formState.emptyBehavior}
        childPerspective={formState.childPerspective}
        availablePerspectives={availablePerspectives}
        lensSystemName={formState.rollupSet.perspectiveSystemName}
        onChange={onChange}
        disabled={disabled}
      />
    </div>
  );
};

RelatedSpheresEditorMolecule.displayName = 'RelatedSpheresEditorMolecule';

export default RelatedSpheresEditorMolecule;
